<script lang="ts" setup>
import {onBeforeUnmount, ref, watch} from 'vue';
import {QBtnDropdown, QItem, QItemSection} from 'quasar';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';

interface FacetOption {
  value: string;
  label: string;
  selected: boolean;
}

// `label` arrives already formatted with its active-count suffix (the organism
// builds it via i18n); this molecule stays purely presentational.
defineProps<{
  label: string;
  selectedCount: number;
  options: FacetOption[];
}>();

const emit = defineEmits<{
  toggle: [value: string];
}>();

const isOpen = ref<boolean>(false);
let scrollListenerTimer = 0;

// Quasar's menu positions once and does not follow an ancestor scroll container, so an
// open dropdown would float detached once the page scrolls. Close it on any outer
// scroll (IPD-2961 #4), ignoring scrolls inside
// the options list itself.
function onOuterScroll(event: Event): void {
  const {target} = event;
  if (target instanceof Element && target.closest('.q-menu')) {
    return;
  }
  isOpen.value = false;
}

function stopScrollWatch(): void {
  window.clearTimeout(scrollListenerTimer);
  window.removeEventListener('scroll', onOuterScroll, {capture: true});
}

watch(isOpen, (open) => {
  if (open) {
    // Defer: opening the dropdown scroll-into-views it, which would otherwise trip the
    // close handler immediately.
    window.clearTimeout(scrollListenerTimer);
    scrollListenerTimer = window.setTimeout(() => {
      window.addEventListener('scroll', onOuterScroll, {capture: true, passive: true});
    }, 200);
  } else {
    stopScrollWatch();
  }
});

onBeforeUnmount(stopScrollWatch);
</script>

<template>
  <q-btn-dropdown
    v-model="isOpen"
    class="facet"
    :class="{'facet--active': selectedCount > 0}"
    flat
    dense
    no-caps
  >
    <template #label>
      <span class="facet__label">{{ label }}</span>
      <svg-illustration name="chevron-down.svg" class="facet__chevron" :class="{'chevron--open': isOpen}"/>
    </template>
    <!--
      Quasar 2.28 stopped putting role="menu" on the dropdown content, so the list
      carries it explicitly; a multi-select menu uses role="menuitemcheckbox" rows
      (aria-checked) as its direct children. The tick is presentational (aria-hidden),
      so the row is the only interactive control — no nested-interactive violation,
      and the row click is the single toggle source.
    -->
    <q-list role="menu">
      <q-item
        v-for="option in options"
        :key="option.value"
        clickable
        role="menuitemcheckbox"
        :aria-checked="option.selected"
        @click="emit('toggle', option.value)"
      >
        <q-item-section side>
          <span class="facet-check" :class="{'facet-check--on': option.selected}" aria-hidden="true">
            <q-icon v-if="option.selected" name="check" class="facet-check__tick"/>
          </span>
        </q-item-section>
        <q-item-section>{{ option.label }}</q-item-section>
      </q-item>
    </q-list>
  </q-btn-dropdown>
</template>

<style lang="scss" scoped>
.facet {
  @include explore-filter-pill;

  // Quasar renders the label inside .q-btn__content; keep its font in sync and
  // add a small gap between the label and the chevron.
  :deep(.q-btn__content) {
    @include font-body-sm-bold;
    gap: var(--Spacing-spacing-1);
  }

  // Replace Quasar's default filled triangle with the DS outline chevron
  // (same treatment as the sign-up dropdowns), rotating when the menu opens.
  :deep(.q-btn-dropdown__arrow) {
    display: none;
  }

  &__chevron {
    @include dropdown-chevron;
  }

  &--active {
    @include explore-filter-pill-active;

    .facet__chevron {
      color: var(--Neutral-UI-neutral-ui-0);
    }
  }
}

// Presentational checkbox for a facet option (the row carries the real
// menuitemcheckbox/aria-checked semantics; this is aria-hidden decoration).
.facet-check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  // Fixed small radius: the --Border-Radius-* tokens are 10-16px (round on an 18px box).
  border-radius: 4px;
  box-shadow: inset 0 0 0 2px var(--Neutral-UI-neutral-ui-40);
  color: var(--Neutral-UI-neutral-ui-0);

  &--on {
    background-color: var(--Primary-primary-color-1);
    box-shadow: none;
  }
}

// Centred tick (flex-centred in the box — no manual offsets to drift).
.facet-check__tick {
  font-size: 14px;
}
</style>
